import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { getProfilePhotoUrl } from '../config';
import { Clock, CheckCircle, XCircle, ClipboardList, Theater, Users, Wrench, Film, FileText, Briefcase, MailOpen, Search, FolderOpen, Inbox, Eye, Phone, Lightbulb, ArrowLeft, RefreshCw, Mail, User } from 'lucide-react';

const ReceivedApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [updating, setUpdating] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const itemsPerPage = 8;
  
  // Ref pour le panneau de détails
  const detailsRef = useRef(null);

  useEffect(() => {
    loadApplications();
  }, []);

  // Reset page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, search]);

  const loadApplications = async () => {
    try {
      const res = await api.get('/applications/received');
      setApplications(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appId, newStatus) => {
    setUpdating(appId);
    try {
      await api.put(`/applications/${appId}`, { statut: newStatus });
      
      const updatedApplications = applications.map(app => 
        app.id === appId ? { ...app, statut: newStatus } : app
      );
      setApplications(updatedApplications);
      
      if (selectedApp?.id === appId) {
        setSelectedApp({ ...selectedApp, statut: newStatus });
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setUpdating(null);
    }
  };

  // Scroll vers les détails
  const handleViewDetails = (app) => {
    setSelectedApp(app);
    setTimeout(() => {
      if (detailsRef.current) {
        detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'en_attente': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'acceptee': return 'bg-green-100 text-green-700 border-green-300';
      case 'refusee': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'en_attente': return 'En attente';
      case 'acceptee': return 'Accepté';
      case 'refusee': return 'Refusé';
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'en_attente': return <Clock className="w-4 h-4" />;
      case 'acceptee': return <CheckCircle className="w-4 h-4" />;
      case 'refusee': return <XCircle className="w-4 h-4" />;
      default: return <ClipboardList className="w-4 h-4" />;
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      acteur: <Theater className="w-4 h-4" />,
      figurant: <Users className="w-4 h-4" />,
      technicien: <Wrench className="w-4 h-4" />,
      realisateur: <Film className="w-4 h-4" />,
      scenariste: <FileText className="w-4 h-4" />,
      producteur: <Briefcase className="w-4 h-4" />,
      autre: <ClipboardList className="w-4 h-4" />
    };
    return icons[type] || <ClipboardList className="w-4 h-4" />;
  };

  // Normalisation pour la recherche
  const normalizeText = (text) => {
    if (!text) return '';
    return text.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ');
  };

  // Filtrage et recherche
  const filteredApplications = useMemo(() => {
    let filtered = [...applications];

    if (filter !== 'all') {
      filtered = filtered.filter(app => app.statut === filter);
    }

    if (search) {
      const searchTerms = normalizeText(search).split(/\s+/).filter(t => t.length > 0);
      filtered = filtered.filter(app => {
        const searchFields = normalizeText(
          `${app.nom || ''} ${app.user_nom || ''} ${app.user_email || ''} ${app.task_titre || ''} ${app.project_titre || ''} ${app.motivation || ''}`
        );
        return searchTerms.every(term => searchFields.includes(term));
      });
    }

    return filtered;
  }, [applications, filter, search]);

  // Pagination
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const paginatedApplications = filteredApplications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.statut === 'en_attente').length,
    accepted: applications.filter(a => a.statut === 'acceptee').length,
    rejected: applications.filter(a => a.statut === 'refusee').length,
  };

  const activeFiltersCount = (filter !== 'all' ? 1 : 0) + (search ? 1 : 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-indigo-50 to-blue-50">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 mx-auto"></div>
            <span className="absolute inset-0 flex items-center justify-center"><MailOpen className="w-6 h-6 text-indigo-600" /></span>
          </div>
          <p className="text-indigo-600 font-medium mt-4">Chargement des candidatures...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-indigo-50 to-blue-50 pb-10">
      {/* Header Hero */}
      <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnYtNGgydjRoNHYyaC00djRoLTJ2LTR6bTAtMzBoLTJ2LTRoMnYtNGgydjRoNHYyaC00djRoLTJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="max-w-7xl mx-auto px-4 py-8 relative">
          <Link to="/author" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition text-sm">
            ← Retour au tableau de bord
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                <span className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center"><MailOpen className="w-6 h-6" /></span>
                Candidatures reçues
              </h1>
              <p className="text-white/80 mt-2">Gérez les candidatures pour vos projets</p>
            </div>
          </div>

          {/* Stats Cards dans Header */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <button 
              onClick={() => setFilter('all')}
              className={`backdrop-blur rounded-xl p-4 text-center border transition-all ${
                filter === 'all' 
                  ? 'bg-white/30 border-white/50 scale-105' 
                  : 'bg-white/10 border-white/20 hover:bg-white/20'
              }`}
            >
              <div className="text-3xl font-bold">{stats.total}</div>
              <div className="text-sm text-white/80">Total</div>
            </button>
            <button 
              onClick={() => setFilter('en_attente')}
              className={`backdrop-blur rounded-xl p-4 text-center border transition-all ${
                filter === 'en_attente' 
                  ? 'bg-yellow-500/30 border-yellow-300/50 scale-105' 
                  : 'bg-yellow-500/20 border-yellow-300/30 hover:bg-yellow-500/30'
              }`}
            >
              <div className="text-3xl font-bold text-yellow-200">{stats.pending}</div>
              <div className="text-sm text-yellow-100/80 flex items-center justify-center gap-1">
                <Clock className="w-4 h-4" /> En attente
              </div>
            </button>
            <button 
              onClick={() => setFilter('acceptee')}
              className={`backdrop-blur rounded-xl p-4 text-center border transition-all ${
                filter === 'acceptee' 
                  ? 'bg-green-500/30 border-green-300/50 scale-105' 
                  : 'bg-green-500/20 border-green-300/30 hover:bg-green-500/30'
              }`}
            >
              <div className="text-3xl font-bold text-green-200">{stats.accepted}</div>
              <div className="text-sm text-green-100/80 flex items-center justify-center gap-1">
                <CheckCircle className="w-4 h-4" /> Acceptées
              </div>
            </button>
            <button 
              onClick={() => setFilter('refusee')}
              className={`backdrop-blur rounded-xl p-4 text-center border transition-all ${
                filter === 'refusee' 
                  ? 'bg-red-500/30 border-red-300/50 scale-105' 
                  : 'bg-red-500/20 border-red-300/30 hover:bg-red-500/30'
              }`}
            >
              <div className="text-3xl font-bold text-red-200">{stats.rejected}</div>
              <div className="text-sm text-red-100/80 flex items-center justify-center gap-1">
                <XCircle className="w-4 h-4" /> Refusées
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-4">
        {/* Barre de recherche sticky */}
        <div className="sticky top-16 z-30 py-2">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-lg p-3 border border-indigo-100">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, projet, tâche..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all"
                />
                {search && (
                  <button 
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl font-medium transition-all ${
                  showFilters || activeFiltersCount > 0
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="hidden sm:inline">Filtres</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-white text-indigo-600 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>

            {showFilters && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">Statut</label>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { value: 'all', label: 'Toutes', count: stats.total },
                      { value: 'en_attente', label: 'En attente', count: stats.pending, IconComp: Clock },
                      { value: 'acceptee', label: 'Acceptées', count: stats.accepted, IconComp: CheckCircle },
                      { value: 'refusee', label: 'Refusées', count: stats.rejected, IconComp: XCircle },
                    ].map(tab => (
                      <button
                        key={tab.value}
                        onClick={() => setFilter(tab.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                          filter === tab.value
                            ? 'bg-indigo-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {tab.IconComp && <tab.IconComp className="w-3 h-3" />} {tab.label} ({tab.count})
                      </button>
                    ))}
                  </div>
                </div>

                {activeFiltersCount > 0 && (
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-gray-500">
                      <span className="font-semibold text-indigo-600">{filteredApplications.length}</span> résultat(s)
                    </span>
                    <button
                      onClick={() => { setSearch(''); setFilter('all'); }}
                      className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      Effacer les filtres
                    </button>
                  </div>
                )}
              </div>
            )}

            {!showFilters && activeFiltersCount > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {search && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                    "{search.substring(0, 15)}{search.length > 15 ? '...' : ''}"
                    <button onClick={() => setSearch('')}>✕</button>
                  </span>
                )}
                {filter !== 'all' && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                    filter === 'en_attente' ? 'bg-yellow-100 text-yellow-700' :
                    filter === 'acceptee' ? 'bg-green-100 text-green-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {getStatusIcon(filter)} {getStatusLabel(filter)}
                    <button onClick={() => setFilter('all')}>✕</button>
                  </span>
                )}
                <button onClick={() => { setSearch(''); setFilter('all'); }} className="text-xs text-gray-400 hover:text-gray-600">
                  Effacer
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Compteur */}
        <div className="flex items-center justify-between mt-4 mb-4">
          <p className="text-gray-600 text-sm">
            <span className="font-bold text-indigo-600 text-lg">{filteredApplications.length}</span> candidature(s)
            {paginatedApplications.length < filteredApplications.length && (
              <span className="text-gray-400"> • Page {currentPage}/{totalPages}</span>
            )}
          </p>
        </div>

        {filteredApplications.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-10 h-10 text-indigo-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Aucune candidature</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {filter === 'all' && !search
                ? "Vous n'avez pas encore reçu de candidatures pour vos projets."
                : "Aucune candidature ne correspond à vos critères de recherche."}
            </p>
            {(filter !== 'all' || search) && (
              <button
                onClick={() => { setSearch(''); setFilter('all'); }}
                className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Liste des candidatures */}
            <div className="space-y-4">
              {paginatedApplications.map((app, index) => (
                <div
                  key={app.id}
                  className={`group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border-l-4 ${
                    app.statut === 'acceptee' ? 'border-green-500' :
                    app.statut === 'refusee' ? 'border-red-500' :
                    'border-yellow-500'
                  } ${selectedApp?.id === app.id ? 'ring-2 ring-indigo-500' : ''}`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="p-4 md:p-5">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {app.photo_profil ? (
                          <img
                            src={getProfilePhotoUrl(app.photo_profil)}
                            alt={app.user_nom || app.nom}
                            className="w-12 h-12 rounded-xl object-cover border-2 border-indigo-100"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                            <span className="text-white font-bold text-lg">
                              {app.user_nom?.[0] || app.nom?.[0] || 'U'}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Contenu */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${getStatusColor(app.statut)}`}>
                            {getStatusIcon(app.statut)} {getStatusLabel(app.statut)}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(app.date_candidature).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short'
                            })}
                          </span>
                        </div>
                        
                        <h3 className="font-bold text-gray-800 truncate">
                          {app.user_nom || app.nom}
                        </h3>
                        
                        <p className="text-sm text-gray-500 truncate flex items-center gap-1">
                          {getTypeIcon(app.task_type)} {app.task_titre || `Tâche #${app.task_id}`}
                        </p>
                        
                        <p className="text-xs text-gray-400 truncate flex items-center gap-1">
                          <FolderOpen className="w-3 h-3" /> {app.project_titre}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                      {app.statut === 'en_attente' && (
                        <>
                          <button
                            onClick={() => updateStatus(app.id, 'acceptee')}
                            disabled={updating === app.id}
                            className="flex-1 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-medium text-sm flex items-center justify-center gap-1.5 disabled:opacity-50"
                          >
                            {updating === app.id ? <Clock className="w-4 h-4 animate-spin" /> : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Accepter
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => updateStatus(app.id, 'refusee')}
                            disabled={updating === app.id}
                            className="flex-1 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition font-medium text-sm flex items-center justify-center gap-1.5 disabled:opacity-50"
                          >
                            {updating === app.id ? <Clock className="w-4 h-4 animate-spin" /> : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Refuser
                              </>
                            )}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleViewDetails(app)}
                        className={`${app.statut === 'en_attente' ? 'px-4' : 'flex-1'} py-2 bg-indigo-100 text-indigo-700 rounded-xl hover:bg-indigo-200 transition font-medium text-sm flex items-center justify-center gap-1.5`}
                      >
                        <Eye className="w-4 h-4" /> Détails & Options
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 py-4">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition font-medium text-sm"
                  >
                    ← Préc
                  </button>
                  
                  <div className="flex gap-1 items-center">
                    {(() => {
                      const pages = [];
                      const showPages = new Set();
                      showPages.add(1);
                      showPages.add(totalPages);
                      for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                        showPages.add(i);
                      }
                      const sortedPages = [...showPages].sort((a, b) => a - b);
                      sortedPages.forEach((page, idx) => {
                        if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                          pages.push(<span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>);
                        }
                        pages.push(
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`w-10 h-10 rounded-xl font-medium transition ${currentPage === page ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white shadow-md hover:shadow-lg'}`}
                          >
                            {page}
                          </button>
                        );
                      });
                      return pages;
                    })()}
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition font-medium text-sm"
                  >
                    Suiv →
                  </button>
                </div>
              )}
            </div>

            {/* Panneau de détails */}
            <div ref={detailsRef} className="lg:sticky lg:top-32 lg:self-start">
              {selectedApp ? (
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 p-6 text-white relative">
                    <button 
                      onClick={() => setSelectedApp(null)}
                      className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition"
                    >
                      ✕
                    </button>
                    
                    <div className="flex items-center gap-4 mb-4">
                      {selectedApp.photo_profil ? (
                        <img
                          src={getProfilePhotoUrl(selectedApp.photo_profil)}
                          alt={selectedApp.nom}
                          className="w-16 h-16 rounded-xl object-cover border-2 border-white/30"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                          <span className="text-2xl font-bold">{selectedApp.nom?.[0] || 'U'}</span>
                        </div>
                      )}
                      <div>
                        <h2 className="text-xl font-bold">{selectedApp.nom}</h2>
                        <p className="text-white/80 text-sm">{selectedApp.user_email}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        selectedApp.statut === 'en_attente' ? 'bg-yellow-400 text-yellow-900' :
                        selectedApp.statut === 'acceptee' ? 'bg-green-400 text-green-900' :
                        'bg-red-400 text-red-900'
                      }`}>
                        {getStatusIcon(selectedApp.statut)} {getStatusLabel(selectedApp.statut)}
                      </span>
                      <span className="text-white/60 text-xs">
                        Candidature du {new Date(selectedApp.date_candidature).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>

                  {/* Poste */}
                  <div className="p-5 bg-gradient-to-r from-indigo-50 to-purple-50 border-b">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getTypeIcon(selectedApp.task_type)}</span>
                      <div>
                        <h3 className="font-bold text-gray-800">{selectedApp.task_titre}</h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1"><FolderOpen className="w-4 h-4" /> {selectedApp.project_titre}</p>
                      </div>
                    </div>
                  </div>

                  {/* Contact */}
                  {selectedApp.contacts && (
                    <div className="p-5 border-b">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center"><Phone className="w-4 h-4 text-blue-600" /></span>
                        Moyens de contact
                      </h4>
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                        <p className="text-blue-800 whitespace-pre-wrap text-sm">{selectedApp.contacts}</p>
                      </div>
                    </div>
                  )}

                  {/* Infos */}
                  {selectedApp.infos && (
                    <div className="p-5 border-b">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center"><Briefcase className="w-4 h-4 text-purple-600" /></span>
                        Infos professionnelles
                      </h4>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">{selectedApp.infos}</p>
                      </div>
                    </div>
                  )}

                  {/* Motivation */}
                  {selectedApp.motivation && (
                    <div className="p-5 border-b">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <span className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center"><Lightbulb className="w-4 h-4 text-amber-600" /></span>
                        Motivation
                      </h4>
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
                        <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed italic">
                          "{selectedApp.motivation}"
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="p-5 bg-gray-50">
                    {selectedApp.statut === 'en_attente' ? (
                      <div className="space-y-3">
                        <p className="text-center text-sm text-yellow-700 bg-yellow-100 rounded-xl py-2 font-medium flex items-center justify-center gap-2">
                          <Clock className="w-4 h-4" /> En attente de votre décision
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => updateStatus(selectedApp.id, 'acceptee')}
                            disabled={updating === selectedApp.id}
                            className="py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {updating === selectedApp.id ? <Clock className="w-5 h-5 animate-spin" /> : (
                              <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Accepter
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => updateStatus(selectedApp.id, 'refusee')}
                            disabled={updating === selectedApp.id}
                            className="py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {updating === selectedApp.id ? <Clock className="w-5 h-5 animate-spin" /> : (
                              <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Refuser
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className={`p-3 rounded-xl text-center font-semibold ${
                          selectedApp.statut === 'acceptee' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {selectedApp.statut === 'acceptee' 
                            ? <><CheckCircle className="w-4 h-4 inline mr-1" /> Candidature acceptée</>
                            : <><XCircle className="w-4 h-4 inline mr-1" /> Candidature refusée</>}
                        </div>
                        <button
                          onClick={() => updateStatus(selectedApp.id, 'en_attente')}
                          disabled={updating === selectedApp.id}
                          className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {updating === selectedApp.id ? <Clock className="w-5 h-5 animate-spin" /> : <><RefreshCw className="w-4 h-4" /> Changer de décision</>}
                        </button>
                      </div>
                    )}

                    {selectedApp.user_email && (
                      <a
                        href={`mailto:${selectedApp.user_email}?subject=Votre candidature pour ${selectedApp.task_titre}`}
                        className="mt-3 w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2 block"
                      >
                        <Mail className="w-4 h-4" /> Contacter par email
                      </a>
                    )}

                    <Link
                      to={`/profile/${selectedApp.user_id}`}
                      className="mt-2 w-full py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-xl transition flex items-center justify-center gap-2 block"
                    >
                      <User className="w-4 h-4" /> Voir le profil complet
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                  <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ArrowLeft className="w-10 h-10 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Sélectionnez une candidature</h3>
                  <p className="text-gray-500 text-sm">
                    Cliquez sur "Détails" pour voir les informations complètes du candidat
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceivedApplications;
